import type { ChatMessage, ChatMessagePart } from '../types';
import { sanitizeStreamingMarkdown } from '../utils/sanitize-streaming-markdown';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CopyWrapper } from './copy-wrapper';
import { ToolCallCard } from './tool-call-card';
import { ReasoningBlock } from './reasoning-block';
import { SourcesBlock } from './sources-block';
import { CitationInline } from './citation-inline';
import { ChainOfThoughtBlock } from './chain-of-thought-block';
import { ConfirmationBlock } from './confirmation-block';
import { ArtifactBlock } from './artifact-block';
import { SandboxBlock } from './sandbox-block';
import { PlanBlock } from './plan-block';
import { CardBlock } from './card-block';
import { CheckpointBlock } from './checkpoint-block';
import { FileTreeBlock } from './file-tree-block';
import { WebPreviewBlock } from './web-preview-block';
import { TestResultsBlock } from './test-results-block';
import { TokenUsage } from './token-usage';
import { InlineChart } from './inline-chart';
import { cn } from '@/lib/utils';
import {
    Copy,
    RefreshCw,
    ThumbsUp,
    ThumbsDown,
    Pencil,
    Trash2,
    Check,
    X,
    ChevronLeft,
    ChevronRight,
    GitBranch,
    AlertCircle,
    Loader2,
    Info,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    Volume2,
    VolumeX,
} from 'lucide-react';
import { memo, useCallback, useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';

const STATUS_ICONS = {
    info: Info,
    warning: AlertTriangle,
    success: CheckCircle2,
    error: XCircle,
};

const STATUS_STYLES = {
    info: 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400',
    warning: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
    success: 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400',
    error: 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400',
};

function CodeBlock({ className, children }: { className?: string; children: React.ReactNode }) {
    const language = className?.replace('language-', '') || '';
    const code = String(children).replace(/\n$/, '');

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        toast.success('Copied');
    };

    return (
        <div className="my-2 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between bg-slate-800 px-3 py-1.5">
                <span className="text-[10px] text-slate-400">{language}</span>
                <button
                    onClick={handleCopy}
                    className="text-[10px] text-slate-400 transition-colors hover:text-slate-200"
                >
                    <Copy className="h-3 w-3" />
                </button>
            </div>
            <pre className="overflow-x-auto bg-slate-900 p-3 text-xs text-slate-200">
                <code>{code}</code>
            </pre>
        </div>
    );
}

const markdownComponents: Components = {
    a({ href, children, node: _node, ...props }) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
            </a>
        );
    },
    pre({ children }) {
        return <>{children}</>;
    },
    code({ children, className }) {
        const isBlock = className?.startsWith('language-');
        if (isBlock) {
            if (className === 'language-chart') {
                return <InlineChart spec={String(children)} />;
            }
            return <CodeBlock className={className}>{children}</CodeBlock>;
        }
        return <code className={className}>{children}</code>;
    },
};

function formatRelativeTime(dateStr?: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function SpeakButton({ text }: { text: string }) {
    const [isSpeaking, setIsSpeaking] = useState(false);

    const handleSpeak = () => {
        if (isSpeaking) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
            return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utterance);
        setIsSpeaking(true);
    };

    return (
        <button
            onClick={handleSpeak}
            className="flex items-center gap-1 text-xs text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
            title={isSpeaking ? 'Stop reading' : 'Read aloud'}
        >
            {isSpeaking ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
        </button>
    );
}

function tableToCsv(headers: string[], rows: string[][]): string {
    const escape = (cell: string) => {
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
            return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
    };
    const lines = [headers.map(escape).join(',')];
    for (const row of rows) {
        lines.push(row.map(escape).join(','));
    }
    return lines.join('\n');
}

function renderPart(
    part: ChatMessagePart,
    index: number,
    context: {
        isStreaming?: boolean;
        isLastMessage?: boolean;
        isUser?: boolean;
        allParts: ChatMessagePart[];
        onSendMessage?: (text: string) => void;
        onRetry?: (messageId: string, options?: { capabilityRef?: string }) => void;
        messageId?: string;
    },
) {
    switch (part.type) {
        case 'text': {
            if (context.isUser) {
                return (
                    <div key={index} className="rounded-2xl bg-blue-600 px-4 py-2.5 text-sm leading-relaxed text-white">
                        <p className="whitespace-pre-wrap">{part.text}</p>
                    </div>
                );
            }
            const markdown = sanitizeStreamingMarkdown(part.text, !!context.isStreaming);
            return (
                <div key={index} className="rounded-2xl bg-slate-50 px-4 py-2.5 text-sm leading-relaxed text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                    <div className="prose prose-sm max-w-none prose-headings:mb-2 prose-headings:mt-3 prose-headings:text-slate-800 prose-p:my-1.5 prose-a:text-blue-600 prose-code:rounded prose-code:bg-slate-200 prose-code:px-1.5 prose-code:py-0.5 prose-code:text-xs prose-code:font-normal prose-code:text-slate-800 prose-code:before:content-none prose-code:after:content-none prose-pre:rounded-lg prose-pre:border prose-pre:border-slate-200 prose-pre:bg-slate-100 prose-pre:text-xs prose-pre:text-slate-800 prose-li:my-0.5 dark:prose-invert">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={markdownComponents}
                        >
                            {markdown}
                        </ReactMarkdown>
                    </div>
                    {context.isStreaming && (
                        <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-slate-600 dark:bg-slate-400" />
                    )}
                </div>
            );
        }

        case 'reasoning': {
            return (
                <div key={index} className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 dark:border-purple-800 dark:bg-purple-950">
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-purple-400">Thinking</p>
                    <p className="text-xs text-purple-700 dark:text-purple-300">{part.text}</p>
                </div>
            );
        }

        case 'chain-of-thought': {
            const isStreamingCoT = context.isStreaming && context.isLastMessage;
            return (
                <ChainOfThoughtBlock
                    key={index}
                    steps={part.steps}
                    isStreaming={isStreamingCoT}
                />
            );
        }

        case 'code':
            return (
                <CopyWrapper key={index} copyText={part.code} label="Copy code" className="my-2">
                    <CodeBlock className={`language-${part.language || ''}`}>{part.code}</CodeBlock>
                </CopyWrapper>
            );

        case 'image':
            return (
                <div key={index} className="my-2">
                    <button className="block">
                        <img
                            src={part.url}
                            alt={part.alt || 'Image'}
                            className="max-h-64 max-w-full rounded-lg border border-slate-200 object-contain shadow-sm transition hover:opacity-90"
                            loading="lazy"
                        />
                    </button>
                </div>
            );

        case 'audio':
            return (
                <div key={index} className="my-2">
                    <audio controls src={part.url} className="w-full max-w-md" preload="metadata">
                        Your browser does not support audio playback.
                    </audio>
                    {part.transcript && (
                        <p className="mt-1 text-xs text-slate-400 italic">{part.transcript}</p>
                    )}
                </div>
            );

        case 'video':
            return (
                <div key={index} className="my-2">
                    <video
                        controls
                        src={part.url}
                        poster={part.thumbnail}
                        className="max-h-80 rounded-lg"
                        preload="metadata"
                    >
                        Your browser does not support video playback.
                    </video>
                </div>
            );

        case 'error':
            return (
                <div key={index} className="rounded-2xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm leading-relaxed dark:border-red-800 dark:bg-red-950">
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        <span>Could not complete request</span>
                    </div>
                    <p className="text-sm text-red-800 dark:text-red-300">{part.message}</p>
                </div>
            );

        case 'tool-call': {
            const hasToolResult = context.allParts.some(
                (p) => p.type === 'tool-result' && p.toolCallId === part.toolCallId,
            );
            if (hasToolResult) return null;
            return (
                <ToolCallCard
                    key={`tc-${part.toolCallId}-${index}`}
                    toolCallId={part.toolCallId}
                    toolName={part.toolName}
                    args={part.args}
                    state={part.state}
                    rawArgString={part.rawArgString}
                />
            );
        }

        case 'tool-result': {
            const matchingCall = context.allParts.find(
                (p): p is Extract<ChatMessagePart, { type: 'tool-call' }> =>
                    p.type === 'tool-call' && p.toolCallId === part.toolCallId,
            );
            if (part.toolName === 'test_execution' && part.result && typeof part.result === 'object' && !part.error) {
                const r = part.result as { passed?: number; failed?: number; skipped?: number; tests?: Array<{ name: string; status: 'passed' | 'failed' | 'skipped'; duration?: number; error?: string; suite?: string }>; duration?: number };
                return (
                    <TestResultsBlock
                        key={`tr-${part.toolCallId}-${index}`}
                        passed={r.passed ?? 0}
                        failed={r.failed ?? 0}
                        skipped={r.skipped ?? 0}
                        tests={r.tests ?? []}
                        duration={r.duration}
                    />
                );
            }
            const hasContentAfter = index < context.allParts.length - 1;
            return (
                <ToolCallCard
                    key={`tr-${part.toolCallId}-${index}`}
                    toolCallId={part.toolCallId}
                    toolName={part.toolName}
                    args={matchingCall?.args ?? {}}
                    state={part.error ? 'output-error' : 'output-available'}
                    rawArgString={matchingCall?.rawArgString}
                    result={part.result}
                    error={part.error}
                    hasContentAfter={hasContentAfter}
                />
            );
        }

        case 'confirmation':
            return (
                <ConfirmationBlock
                    key={index}
                    toolCallId={part.toolCallId}
                    toolName={part.toolName}
                    message={part.message}
                    state={part.state}
                />
            );

        case 'source': {
            const allSources = context.allParts.filter(
                (p): p is Extract<ChatMessagePart, { type: 'source' }> => p.type === 'source',
            );
            const isFirst = context.allParts.indexOf(part) ===
                context.allParts.findIndex((p) => p.type === 'source');
            if (!isFirst) return null;
            return <SourcesBlock key={index} sources={allSources} />;
        }

        case 'citation':
            return <CitationInline key={index} citations={part.citations} />;

        case 'suggestion':
            return (
                <div key={index} className="my-2 flex flex-wrap gap-2" role="group" aria-label="Suggestions">
                    {part.suggestions.map((s, i) => (
                        <button
                            key={i}
                            type="button"
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                            onClick={() => context.onSendMessage?.(s)}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            );

        case 'status': {
            const StatusIcon = STATUS_ICONS[part.state] ?? Info;
            return (
                <div
                    key={index}
                    role="status"
                    className={`my-2 flex items-start gap-2 rounded-lg border p-3 text-sm ${STATUS_STYLES[part.state] ?? STATUS_STYLES.info}`}
                >
                    <StatusIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="flex-1">
                        {part.message}
                        {part.progress !== undefined && (
                            <div
                                className="mt-2 h-1.5 w-full rounded-full bg-current/10"
                                role="progressbar"
                                aria-valuenow={part.progress}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            >
                                <div
                                    className="h-full rounded-full bg-current transition-all"
                                    style={{ width: `${part.progress}%` }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        case 'file': {
            const isImage = part.mediaType?.startsWith('image/');
            if (isImage) {
                return (
                    <div key={index} className="my-1">
                        <button className="block">
                            <img
                                src={part.url}
                                className="max-h-64 max-w-full rounded-lg border border-slate-200 object-contain shadow-sm transition hover:opacity-90"
                                alt={part.filename}
                            />
                        </button>
                        <p className="mt-1 text-[10px] text-slate-400">{part.filename}</p>
                    </div>
                );
            }
            return (
                <div key={index} className="my-1 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-700 dark:bg-blue-950">
                    <span className="text-blue-500">📎</span>
                    <a href={part.url} download={part.filename} className="truncate text-xs text-blue-800 hover:underline dark:text-blue-200">
                        {part.filename}
                    </a>
                    {part.size && (
                        <span className="text-[10px] text-slate-400">({(part.size / 1024).toFixed(1)} KB)</span>
                    )}
                </div>
            );
        }

        case 'table':
            return (
                <CopyWrapper
                    key={index}
                    copyText={tableToCsv(part.headers, part.rows)}
                    label="Copy as CSV"
                    className="my-2"
                >
                    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                        <table className="min-w-full border-collapse text-xs">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-800">
                                    {part.headers.map((h, i) => (
                                        <th key={i} className="border-b border-slate-200 px-3 py-2 text-left font-medium text-slate-700 dark:border-slate-700 dark:text-slate-300">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {part.rows.map((row, ri) => (
                                    <tr key={ri} className={ri % 2 === 1 ? 'bg-slate-50/50 dark:bg-slate-800/50' : ''}>
                                        {row.map((cell, ci) => (
                                            <td key={ci} className="border-b border-slate-100 px-3 py-1.5 text-slate-600 dark:border-slate-700 dark:text-slate-400">
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {part.caption && (
                            <p className="px-3 py-1.5 text-[10px] text-slate-400">{part.caption}</p>
                        )}
                    </div>
                </CopyWrapper>
            );

        case 'terminal':
            return (
                <CopyWrapper key={index} copyText={part.output} label="Copy output" className="my-2">
                    <div className="overflow-hidden rounded-lg border border-slate-700">
                        <div className="bg-slate-800 px-3 py-1.5">
                            <span className="text-[10px] text-slate-400">Terminal</span>
                        </div>
                        <div className="bg-slate-900 p-3 text-xs">
                            {part.command && (
                                <div className="mb-1 font-mono text-slate-400">$ {part.command}</div>
                            )}
                            <pre className="whitespace-pre-wrap font-mono text-slate-200">
                                {part.output}
                                {part.isStreaming && (
                                    <span className="inline-block h-3 w-1 animate-pulse bg-slate-200 align-middle" />
                                )}
                            </pre>
                            {part.exitCode !== undefined && (
                                <div className={`mt-2 text-[10px] font-mono ${part.exitCode === 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    exit {part.exitCode}
                                </div>
                            )}
                        </div>
                    </div>
                </CopyWrapper>
            );

        case 'artifact':
            if (part.artifactType === 'html') {
                return <WebPreviewBlock key={index} html={part.content} title={part.title} />;
            }
            return (
                <ArtifactBlock
                    key={index}
                    id={part.id}
                    title={part.title}
                    description={part.description}
                    content={part.content}
                    language={part.language}
                    artifactType={part.artifactType}
                />
            );

        case 'sandbox':
            return (
                <SandboxBlock
                    key={index}
                    code={part.code}
                    language={part.language}
                    filename={part.filename}
                    output={part.output}
                    error={part.error}
                    state={part.state}
                    renderStackTrace
                />
            );

        case 'diff':
            return (
                <CopyWrapper key={index} copyText={part.newCode} label="Copy new code" className="my-2">
                    <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                        {part.filename && (
                            <div className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                                {part.filename}
                            </div>
                        )}
                        <pre className="overflow-x-auto p-3 text-xs">
                            {part.oldCode.split('\n').map((l, i) => (
                                <div key={`old-${i}`} className="text-red-600 dark:text-red-400">- {l}</div>
                            ))}
                            {part.newCode.split('\n').map((l, i) => (
                                <div key={`new-${i}`} className="text-green-600 dark:text-green-400">+ {l}</div>
                            ))}
                        </pre>
                    </div>
                </CopyWrapper>
            );

        case 'plan':
            return <PlanBlock key={index} steps={part.steps} />;

        case 'card':
            return (
                <CardBlock
                    key={index}
                    url={part.url}
                    title={part.title}
                    description={part.description}
                    image={part.image}
                    siteName={part.siteName}
                />
            );

        case 'checkpoint':
            return <CheckpointBlock key={index} label={part.label} />;

        case 'file-tree':
            return <FileTreeBlock key={index} tree={part.tree} />;

        default:
            return (
                <div key={index} className="my-1 text-xs text-slate-400">
                    [Unsupported part type: {(part as ChatMessagePart).type}]
                </div>
            );
    }
}

function getMessageText(message: ChatMessage): string {
    return message.parts
        .map((p) => {
            if (p.type === 'text') return p.text;
            if (p.type === 'code') return p.code;
            return '';
        })
        .filter(Boolean)
        .join('\n\n');
}

type MessageSendStatus = 'sending' | 'confirmed' | 'failed';

interface MessageRendererProps {
    message: ChatMessage;
    isStreaming?: boolean;
    isLastMessage?: boolean;
    branchInfo?: {
        forkParentId: string;
        currentBranch: number;
        totalBranches: number;
    };
    sendStatus?: MessageSendStatus;
    onEdit?: (messageId: string, newText: string) => void;
    onDelete?: (messageId: string, deleteBelow?: boolean) => void;
    onSwitchBranch?: (forkParentId: string, branchIndex: number) => void;
    onRetry?: (messageId: string, options?: { capabilityRef?: string }) => void;
    onRetrySend?: (messageId: string) => void;
    onFork?: (messageId: string) => void;
    onCopyLink?: (messageId: string) => void;
    onSendMessage?: (text: string) => void;
}

export const MessageRenderer = memo(function MessageRenderer({
    message,
    isStreaming,
    isLastMessage,
    branchInfo,
    sendStatus,
    onEdit,
    onDelete,
    onSwitchBranch,
    onRetry,
    onRetrySend,
    onFork,
    onCopyLink,
    onSendMessage,
}: MessageRendererProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(getMessageText(message));
        toast.success('Copied to clipboard');
    }, [message]);

    const handleStartEdit = useCallback(() => {
        setEditText(getMessageText(message));
        setIsEditing(true);
    }, [message]);

    const handleCancelEdit = useCallback(() => {
        setIsEditing(false);
        setEditText('');
    }, []);

    const handleSaveEdit = useCallback(() => {
        const trimmed = editText.trim();
        if (!trimmed || trimmed === getMessageText(message)) {
            handleCancelEdit();
            return;
        }
        onEdit?.(message.id, trimmed);
        setIsEditing(false);
        setEditText('');
    }, [editText, message, onEdit, handleCancelEdit]);

    const handleEditKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Escape') handleCancelEdit();
            else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSaveEdit();
            }
        },
        [handleCancelEdit, handleSaveEdit],
    );

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.selectionStart = textareaRef.current.value.length;
        }
    }, [isEditing]);

    const handleBranchPrevious = useCallback(() => {
        if (!branchInfo || !onSwitchBranch) return;
        const prev = branchInfo.currentBranch > 0 ? branchInfo.currentBranch - 1 : branchInfo.totalBranches - 1;
        onSwitchBranch(branchInfo.forkParentId, prev);
    }, [branchInfo, onSwitchBranch]);

    const handleBranchNext = useCallback(() => {
        if (!branchInfo || !onSwitchBranch) return;
        const next = branchInfo.currentBranch < branchInfo.totalBranches - 1 ? branchInfo.currentBranch + 1 : 0;
        onSwitchBranch(branchInfo.forkParentId, next);
    }, [branchInfo, onSwitchBranch]);

    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';
    const isSending = sendStatus === 'sending';
    const isFailed = sendStatus === 'failed';

    const partContext = {
        isStreaming,
        isLastMessage,
        isUser,
        allParts: message.parts,
        onSendMessage,
        onRetry: isAssistant ? onRetry : undefined,
        messageId: message.id,
    };

    if (isEditing) {
        return (
            <div className="flex justify-end">
                <div className="max-w-[75%] space-y-2">
                    <textarea
                        ref={textareaRef}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        className="min-h-[80px] w-full resize-y rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        placeholder="Edit your message..."
                    />
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleSaveEdit}
                            disabled={!editText.trim()}
                            className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                        >
                            <Check className="mr-1 inline h-3 w-3" />
                            Save & Resend
                        </button>
                        <button
                            onClick={handleCancelEdit}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            id={`message-${message.id}`}
            className={cn('group flex w-full gap-3', isUser && 'justify-end')}
            role="article"
            aria-label={`${message.role} message`}
        >
            {/* Assistant avatar */}
            {isAssistant && (
                <div className="mt-1 flex shrink-0 flex-col items-center gap-1">
                    <div
                        className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-sm"
                        style={{ background: 'linear-gradient(to bottom right, #3b82f6, #4f46e5)' }}
                    >
                        AI
                    </div>
                    {message.capabilityRef && (
                        <span className="max-w-[80px] text-center text-[10px] font-medium leading-tight text-slate-600 dark:text-slate-400">
                            {message.capabilityRef.split('/').pop()?.split(':')[0] || 'Agent'}
                        </span>
                    )}
                </div>
            )}

            {/* Content */}
            <div className={cn('max-w-[85%] space-y-1', isUser && 'max-w-[75%]')}>
                {message.parts.length === 0 && isStreaming && (
                    <div className="rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
                        <div className="flex items-center gap-1">
                            <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: '0ms' }} />
                            <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: '150ms' }} />
                            <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: '300ms' }} />
                        </div>
                    </div>
                )}

                {message.parts.map((part, i) => renderPart(part, i, partContext))}

                {isSending && (
                    <span className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Sending...
                    </span>
                )}
                {isFailed && (
                    <div className="mt-1 flex items-center gap-2 text-xs text-red-500">
                        <AlertCircle className="h-3 w-3" />
                        <span>Failed to send</span>
                        {onRetrySend && (
                            <button className="underline hover:no-underline" onClick={() => onRetrySend(message.id)} type="button">
                                Retry
                            </button>
                        )}
                    </div>
                )}

                {/* Interrupted banner */}
                {isAssistant && !isStreaming && message.parts.some(p => p.type === 'status' && p.message === 'Generation stopped') && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950">
                        <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                        <span className="text-xs text-amber-700 dark:text-amber-300">Response interrupted</span>
                        {onRetry && (
                            <button
                                onClick={() => onRetry(message.id)}
                                className="ml-auto text-xs font-medium text-amber-700 hover:text-amber-900 dark:text-amber-400"
                            >
                                Retry
                            </button>
                        )}
                    </div>
                )}

                {/* Actions */}
                {!isStreaming && (
                    <>
                        {isUser && (
                            <div className="mt-0.5 flex items-center justify-end gap-1.5">
                                <button
                                    onClick={handleStartEdit}
                                    className="rounded p-1 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100 dark:hover:bg-slate-800"
                                    title="Edit"
                                >
                                    <Pencil className="h-3 w-3" />
                                </button>
                                {onFork && (
                                    <button
                                        onClick={() => onFork(message.id)}
                                        className="rounded p-1 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100 dark:hover:bg-slate-800"
                                        title="Branch"
                                    >
                                        <GitBranch className="h-3 w-3" />
                                    </button>
                                )}
                                <span className="text-[10px] text-slate-300">{formatRelativeTime(message.createdAt)}</span>
                            </div>
                        )}

                        {isAssistant && (
                            <div className="mt-1 flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                                {onRetry && (
                                    <button
                                        onClick={() => onRetry(message.id)}
                                        className="flex items-center gap-1 text-xs text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
                                        title="Regenerate"
                                    >
                                        <RefreshCw className="h-3 w-3" /> Regenerate
                                    </button>
                                )}
                                <button
                                    onClick={handleCopy}
                                    className="flex items-center gap-1 text-xs text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
                                    title="Copy"
                                >
                                    <Copy className="h-3 w-3" /> Copy
                                </button>
                                <SpeakButton text={getMessageText(message)} />
                                <button className="flex items-center gap-1 text-xs text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200" title="Good response">
                                    <ThumbsUp className="h-3 w-3" />
                                </button>
                                <button className="flex items-center gap-1 text-xs text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200" title="Bad response">
                                    <ThumbsDown className="h-3 w-3" />
                                </button>
                                {onFork && (
                                    <button
                                        onClick={() => onFork(message.id)}
                                        className="flex items-center gap-1 text-xs text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
                                        title="Branch"
                                    >
                                        <GitBranch className="h-3 w-3" />
                                    </button>
                                )}
                                <span className="ml-auto text-[10px] text-slate-300">{formatRelativeTime(message.createdAt)}</span>
                            </div>
                        )}

                        {/* Branch navigation */}
                        {branchInfo && branchInfo.totalBranches > 1 && (
                            <div className="mt-1 flex items-center gap-0.5">
                                <button
                                    onClick={handleBranchPrevious}
                                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                                    aria-label="Previous branch"
                                >
                                    <ChevronLeft className="h-3 w-3" />
                                </button>
                                <span className="px-1 text-[10px] text-slate-400 tabular-nums">
                                    {branchInfo.currentBranch + 1}/{branchInfo.totalBranches}
                                </span>
                                <button
                                    onClick={handleBranchNext}
                                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                                    aria-label="Next branch"
                                >
                                    <ChevronRight className="h-3 w-3" />
                                </button>
                            </div>
                        )}
                    </>
                )}

                {isAssistant && !isStreaming && message.usage && (
                    <TokenUsage usage={message.usage} model={message.model} />
                )}
            </div>

            {/* User avatar (right side) */}
            {isUser && (
                <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600 shadow-sm dark:bg-slate-700 dark:text-slate-300">
                    U
                </div>
            )}
        </div>
    );
}, (prev, next) => {
    if (prev.isStreaming || next.isStreaming) return false;
    return prev.message.id === next.message.id
        && prev.sendStatus === next.sendStatus
        && prev.branchInfo === next.branchInfo;
});
